/**
 * Authentication utilities
 * Provides functions for token management using cookies
 */

import { setCookie, getCookie, removeCookie } from "./cookies"

/**
 * Sets the authentication token in cookies
 * @param token - JWT token
 * @returns boolean indicating success
 */
export const setToken = (token: string): boolean => {
  try {
    const success = setCookie("token", token, {
      // Set expiration to 7 days from now
      expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString(),
    })

    if (!success) {
      throw new Error("Failed to set token as cookie")
    }

    // Verify token was stored
    const storedToken = getToken()
    if (!storedToken) {
      throw new Error("Failed to verify token storage")
    }

    return true
  } catch (error) {
    console.error("Error in setToken:", error)
    return false
  }
}

/**
 * Sets the refresh token in cookies
 * @param refreshToken - Refresh token
 * @returns boolean indicating success
 */
export const setRefreshToken = (refreshToken: string): boolean => {
  try {
    const success = setCookie("refreshToken", refreshToken, {
      // Set expiration to 30 days from now for refresh token
      expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toUTCString(),
    })

    if (!success) {
      throw new Error("Failed to set refresh token as cookie")
    }

    return true
  } catch (error) {
    console.error("Error in setRefreshToken:", error)
    return false
  }
}

/**
 * Gets the authentication token from cookies
 * @returns Token string or null if not found
 */
export const getToken = (): null | string => {
  return getCookie("token")
}

/**
 * Gets the refresh token from cookies
 * @returns Refresh token string or null if not found
 */
export const getRefreshToken = (): null | string => {
  return getCookie("refreshToken")
}

/**
 * Removes both authentication and refresh tokens
 */
export const removeTokens = (): void => {
  try {
    removeCookie("token")
    removeCookie("refreshToken")
  } catch (error) {
    console.error("Error removing tokens:", error)
  }
}

/**
 * Checks if user is authenticated (has valid token)
 * @returns boolean indicating if user has a token
 */
export const isAuthenticated = (): boolean => {
  return getToken() !== null
}

/**
 * Parses JWT token payload without verification
 * @param token - JWT token
 * @returns Parsed payload or null if invalid
 */
export const parseTokenPayload = (token: string): Record<string, unknown> | null => {
  try {
    const tokenParts = token.split(".")
    if (tokenParts.length !== 3) {
      throw new Error("Invalid token format")
    }

    // More robust base64 decoding
    const base64Url = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/")
    const decodedPayload = decodeURIComponent(
      Array.from(atob(base64Url))
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    )

    return JSON.parse(decodedPayload)
  } catch (error) {
    console.error("Token parsing error:", error)
    return null
  }
}

/**
 * Checks if token is expired
 * @param token - JWT token (optional, uses stored token if not provided)
 * @returns boolean indicating if token is expired
 */
export const isTokenExpired = (token?: string): boolean => {
  try {
    const tokenToCheck = token || getToken()
    if (!tokenToCheck) {
      return true
    }

    const payload = parseTokenPayload(tokenToCheck)
    if (!payload || !payload.exp) {
      return true
    }

    return Date.now() >= Number(payload.exp) * 1000
  } catch (error) {
    console.error("Error checking token expiration:", error)
    return true
  }
}
